import { useState } from 'react';
import { css } from '@emotion/react';
import { TruncatableHeaderTitle } from '@frontend/analytics-shared';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { SwitchChipGroup, useSwitchChipGroup } from '@frontend/design-system';
import { usePaNavigate } from './hooks/use-pa-navigate';
import { HygieneFollowUpMetric } from './hygiene-follow-up-metric';
import { HygieneTreatmentMetric } from './hygiene-treatment-metric';
import { RestorativeTreatmentMetric } from './restorative-treatment-metric';

type Chart = 'hygiene-follow-up' | 'hygiene-treatment' | 'restorative-treatment';
const defaultChart: Chart = 'hygiene-treatment';

export const SmallModeTreatmentPlans = () => {
  const { t } = useTranslation('analytics');
  const [chart, setChart] = useState<Chart>(defaultChart);
  const navigate = usePaNavigate();

  const switchChipProps = useSwitchChipGroup({
    defaultValue: [defaultChart],
    onChange: (val) => {
      setChart(val[0] as Chart);
    },
  });

  const handleCardClick = () => {
    navigate({
      to: `/analytics/practice/${chart}`,
    });
  };

  const handleCardKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === 'Space') {
      handleCardClick();
    }
  };

  return (
    <section
      css={styles.mainWrapper}
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
      role='button'
      tabIndex={0}
      data-trackingid={`pa-small-mode-${chart}-deep-dive`}
    >
      <TruncatableHeaderTitle title={t('Treatment Overview')} style={{ marginBottom: theme.spacing(2) }} />
      <SwitchChipGroup {...switchChipProps} singleSelect>
        <SwitchChipGroup.Option trackingId='pa-small-mode-hygiene-treatment' value='hygiene-treatment'>
          {t('Hygiene')}
        </SwitchChipGroup.Option>
        <SwitchChipGroup.Option trackingId='pa-small-mode-restorative-treatment' value='restorative-treatment'>
          {t('Restorative')}
        </SwitchChipGroup.Option>
        <SwitchChipGroup.Option trackingId='pa-small-mode-hygiene-follow-up' value='hygiene-follow-up'>
          {t('Hygiene Follow-Up')}
        </SwitchChipGroup.Option>
      </SwitchChipGroup>
      <div css={styles.chartWrapper}>
        {chart === 'hygiene-treatment' && <HygieneTreatmentMetric />}
        {chart === 'restorative-treatment' && <RestorativeTreatmentMetric />}
        {chart === 'hygiene-follow-up' && <HygieneFollowUpMetric />}
      </div>
    </section>
  );
};

const styles = {
  mainWrapper: css`
    border-radius: ${theme.borderRadius.medium};
    border: 1px solid ${theme.colors.neutral20};
    padding: ${theme.spacing(2)};

    &:hover {
      background-color: ${theme.colors.neutral5};
      cursor: pointer;
    }
  `,

  chartWrapper: css`
    margin-top: ${theme.spacing(3)};
  `,
};
